import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
    ActionGetCCPaperDetail,
    ActionGetMedicineList,
    GetMedicalDetailAction,
} from '../store/Actions/PatientAction';
import {
    ConsultationMasterDataAction,
    CreateConsultationMedicalDetailsAction,
    getAppointmentById
} from '../store/Actions/AppointmentAction';
import {
    UpdateAppointmentNotesApi,
    UpdateByCustomerIdApi
} from '../utils/api';
import { ActionGetMedicalDraft } from '../store/Actions/MedicalHistoryAction';
import { removeStorage } from '../utils/storage';

// --- Constants & Helpers ---

const cleanPayload = (data) => {
    const preserveKeys = ['customerId', 'appointmentId', 'status', 'consultingFor', 'dateOfConsultation', '_id'];

    // Helper to check if a value is effectively "empty"
    const isEmpty = (val) => {
        if (val === null || val === undefined) return true;
        if (typeof val === 'string') return val.trim() === '';
        if (Array.isArray(val)) return val.length === 0;
        if (typeof val === 'object' && !(val instanceof Date)) return Object.keys(val).length === 0;
        return false;
    };

    const clean = (obj) => {
        // Handle Arrays
        if (Array.isArray(obj)) {
            const cleanedItems = obj
                .map(item => (typeof item === 'object' ? clean(item) : item))
                .filter(item => !isEmpty(item));
            return cleanedItems;
        }

        // Handle Objects
        if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
            const cleanedObj = {};
            Object.keys(obj).forEach(key => {
                // Always keep specific control keys
                if (preserveKeys.includes(key)) {
                    cleanedObj[key] = obj[key];
                    return;
                }

                let value = obj[key];

                // Recursively clean nested objects/arrays
                if (value !== null && typeof value === 'object') {
                    value = clean(value);
                }

                // Only add to result if not empty
                if (!isEmpty(value)) {
                    cleanedObj[key] = value;
                }
            });
            return cleanedObj;
        }

        return obj;
    };

    return clean(data);
};

const EMPTY_MEDICINE = {
    medicineName: '',
    days: null,
    whenToTake: [],
    anupan: '',
    howMuch: [''],
    course: '',
};

const DEFAULT_FORM_STATE = {
    nextConsultationDate: null,
    yakrut: [],
    pleeha: [],
    sparshaSahatva: [
        { Yakrut: [] },
        { Pleeha: [] },
        { 'Dakshi Vruka': [] },
        { 'Vaam Vruka': [] },
        { 'Apan Kaksha': [] },
    ],
    recomendedMedicines: [EMPTY_MEDICINE],
    laboratory: [],
    advice: [],
    dateOfMedicalStore: null,
    rootCause: [],
    doctorNotes: [''],
    panchakrama: [],
    jivha: [],
    laboratoryNote: '',
    upshay: '',
    anupshay: '',
    nails: '',
    eyes: '',
    prakruti: [],
    dos: [],
    donts: [],
    virechan: [],
    exercise: [],
    nadi: [],
    naad: [
        { Yakrut: '' },
        { Pliha: '' }
    ],
    diagnosis: [{ diagnosis: '', other: '' }],
    treatment: [{ treatmentName: '', other: '', day: '' }],
    solution: [{ solutionName: '', other: '', day: '' }],
};

/**
 * Custom hook for managing appointment details page state and logic
 */
export const useAppointmentDetails = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    // URL Parameters
    const params = {
        page: searchParams.get('page'),
        userId: searchParams.get('userId'),
        appointmentId: searchParams.get('appointmentId'),
        consultingFor: searchParams.get('consultingFor'),
        consultationId: searchParams.get('consultationId'),
        status: searchParams.get('status'),
    };
    const patientId = params.userId || '';

    // Local State
    const [notes, setNotes] = useState('');
    const [isEditingChiefComplaint, setIsEditingChiefComplaint] = useState(false);
    const [tempChiefComplaint, setTempChiefComplaint] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);

    // Redux State
    const { medicineList, loader: patientLoader, MedicalDetail, CcPaperDetail } = useSelector(state => state?.patient);
    const { userDetails } = useSelector(state => state?.auth);
    const { ConsultationMasterData, AppointmentById, loader: appointmentLoader } = useSelector(state => state?.Appointment);

    // Combined loader for full-page coverage
    const loader = patientLoader || appointmentLoader;
    const { medicalDraft, medicalDraftLoader } = useSelector(state => state?.MedicalHistory);

    // Derived State
    const AppointmentData = AppointmentById?.[0];
    const IsFilledForm = MedicalDetail?.some(item => item?.status === 'Completed');
    const medicalRecord = MedicalDetail?.[0] || {};
    const draft = useMemo(() => (!IsFilledForm && medicalDraft?.length > 0 ? medicalDraft[0] : null), [IsFilledForm, medicalDraft]);

    // Data Fetching
    useEffect(() => {
        if (params.consultationId) dispatch(GetMedicalDetailAction({ Query: `/${params.consultationId}` }));
        if (params.appointmentId) {
            dispatch(getAppointmentById(params.appointmentId));
            dispatch(ActionGetMedicalDraft({ id: params.appointmentId }));
        }
        dispatch(ActionGetMedicineList({ Query: '' }));
        dispatch(ConsultationMasterDataAction({ Query: '' }));
        if (patientId) dispatch(ActionGetCCPaperDetail({ Query: `/${patientId}` }));
    }, [params.consultationId, params.appointmentId, patientId, dispatch]);

    // Initialize notes
    useEffect(() => {
        if (AppointmentData?.notes) setNotes(AppointmentData.notes);
    }, [AppointmentData]);

    // Patient Information Extraction
    const patientInfo = useMemo(() => {
        const customer = medicalRecord?.customerId || AppointmentData?.customerId || {};
        const appointment = AppointmentData || {};

        return {
            name: customer.name || appointment.customerName,
            phoneNo: customer.phoneNo || appointment.phoneNo,
            gender: customer.gender,
            id: customer._id,
            birthDate: customer.birthDate,
            registrationNo: customer.registrationNo || appointment.registrationNo,
            dateOfConsultation: customer.dateOfConsultation || appointment.date,
            consultatingDoctor: medicalRecord?.appointmentId?.doctor?.name || medicalRecord?.adminId?.name || appointment.doctorName,
            treatmentDetails: appointment.treatment ||
                (medicalRecord?.treatment?.[0] && `${medicalRecord.treatment[0].treatmentName} (Day ${medicalRecord.treatment[0].treatmentsDay})`),
        };
    }, [medicalRecord, AppointmentData]);

    // Form Initial Values Logic
    const initialValues = useMemo(() => {
        // Deep clone to prevent Redux state mutation errors
        const sourceData = draft || medicalRecord;
        const source = JSON.parse(JSON.stringify(sourceData || {}));

        // Helper to format date for Formik/DayJS
        const formatDate = (date) => (date ? dayjs(date) : null);

        // Helper to extract titles from object arrays (e.g., tags/master data)
        const getTitles = (arr) => arr?.map(v => v?.title || v) || [];

        // Combine API data with full skeleton
        const baseValues = {
            ...DEFAULT_FORM_STATE,
            ...source,
            nextConsultationDate: formatDate(source.nextConsultationDate),
            dateOfMedicalStore: formatDate(source.dateOfMedicalStore),

            // Ensure complex objects/arrays have their structure even if empty from API
            yakrut: source.yakrut || DEFAULT_FORM_STATE.yakrut,
            pleeha: source.pleeha || DEFAULT_FORM_STATE.pleeha,
            sparshaSahatva: source.sparshaSahatva?.length > 0 ? source.sparshaSahatva : DEFAULT_FORM_STATE.sparshaSahatva,
            recomendedMedicines: source.recomendedMedicines?.length > 0 ? source.recomendedMedicines : DEFAULT_FORM_STATE.recomendedMedicines,
            laboratory: source.laboratory || DEFAULT_FORM_STATE.laboratory,
            advice: source.advice || DEFAULT_FORM_STATE.advice,
            rootCause: source.rootCause || DEFAULT_FORM_STATE.rootCause,
            doctorNotes: source.doctorNotes?.length > 0 ? source.doctorNotes : DEFAULT_FORM_STATE.doctorNotes,
            panchakrama: source.panchakrama || DEFAULT_FORM_STATE.panchakrama,
            jivha: source.jivha || DEFAULT_FORM_STATE.jivha,
            dos: getTitles(source.dos),
            donts: getTitles(source.donts),
            virechan: Array.isArray(source.virechan) ? source.virechan : (source.virechan ? [source.virechan] : DEFAULT_FORM_STATE.virechan),
            exercise: getTitles(source.exercise),
            nadi: source.nadi || DEFAULT_FORM_STATE.nadi,
            naad: source.naad?.length > 0 ? source.naad : DEFAULT_FORM_STATE.naad,
            diagnosis: source.diagnosis?.length > 0 ? source.diagnosis : DEFAULT_FORM_STATE.diagnosis,
            treatment: source.treatment?.length > 0 ? source.treatment.map(t => ({ ...t, treatmentsDay: t.treatmentsDay || t.day || '' })) : DEFAULT_FORM_STATE.treatment,
            solution: source.solution?.length > 0 ? source.solution.map(s => ({ ...s, treatmentsDay: s.treatmentsDay || s.day || '' })) : DEFAULT_FORM_STATE.solution,

            // Control identifiers
            customerId: patientId,
            appointmentId: params.appointmentId,
            status: 'Draft',
            consultingFor: params.consultingFor,
            dateOfConsultation: new Date(),
            _id: source._id || source.id,
        };

        return baseValues;
    }, [draft?._id, medicalRecord?._id, patientId, params.appointmentId, params.consultingFor, IsFilledForm]);

    // Submission Logic
    const handleFormSubmit = (values, formikBag) => {
        setPendingSubmission({ values: { ...values }, formikBag });
        setShowStatusModal(true);
    };

    const handleStatusSelection = async (selectedStatus) => {
        if (!pendingSubmission) return;

        const { values, formikBag } = pendingSubmission;
        const { setSubmitting, setErrors } = formikBag;
        setShowStatusModal(false);

        // 1. Contextual Updates (Chief Complaint & Notes)
        if (isEditingChiefComplaint && tempChiefComplaint.trim()) {
            try {
                const updatedDetails = {
                    ...CcPaperDetail,
                    personalDetails: CcPaperDetail?.personalDetails?.map(item =>
                        item?.personalDetilsKey === 'ChiefComplaints' ? { ...item, value: tempChiefComplaint } : item
                    ),
                };
                await UpdateByCustomerIdApi({ customerId: patientInfo.id, obj: updatedDetails });
                setIsEditingChiefComplaint(false);
            } catch (e) { console.error('CC Update failed', e); }
        }

        if (notes?.trim()) {
            try { await UpdateAppointmentNotesApi(params.appointmentId, notes); }
            catch (e) { console.error('Notes Update failed', e); }
        }

        // 2. Payload Preparation
        const mapToMasterId = (selectedTitles, masterList) =>
            (masterList || []).filter(m => selectedTitles?.includes(m.title)).map(({ id, title }) => ({ id, title }));

        const rawPayload = {
            ...values,
            status: selectedStatus,
            nextConsultationDate: values.nextConsultationDate ? dayjs(values.nextConsultationDate).toISOString() : null,
            dateOfMedicalStore: values.dateOfMedicalStore ? dayjs(values.dateOfMedicalStore).toISOString() : null,
            dos: mapToMasterId(values.dos, ConsultationMasterData?.doAndDonts),
            donts: mapToMasterId(values.donts, ConsultationMasterData?.doAndDonts),
            exercise: mapToMasterId(values.exercise, ConsultationMasterData?.exercises),
            virechan: values.virechan ? (Array.isArray(values.virechan) ? values.virechan : [values.virechan]) : [],
        };

        const finalPayload = {
            obj: cleanPayload(rawPayload)
        };

        // 3. Dispatch Action
        dispatch(CreateConsultationMedicalDetailsAction(finalPayload, res => {
            if (res?.statusCode === 200 || res?.statusCode === 201) {
                removeStorage(`patient_draft_${params.appointmentId}`);
                if (document.referrer) {
                    window.location.href = document.referrer;
                } else {
                    window.history.back();
                }
            } else if (res?.statusCode === 400) {
                window.scrollTo({ top: 10, behavior: 'smooth' });
                setErrors(res?.data?.errors);
            }
            setSubmitting(false);
            setPendingSubmission(null);
        }));
    };

    const handleCancelStatusModal = (formikBag) => {
        setShowStatusModal(false);
        if (formikBag) {
            formikBag.setSubmitting(false);
        } else if (pendingSubmission?.formikBag) {
            pendingSubmission.formikBag.setSubmitting(false);
        }
        setPendingSubmission(null);
    };

    const handleUpdatePatientForm = async (payload) => {
        const res = await UpdateByCustomerIdApi({ customerId: patientId, obj: payload });
        if (res?.statusCode === 200) dispatch(ActionGetCCPaperDetail({ Query: `/${patientId}` }));
        return res;
    };

    return {
        // URL Parameters
        ParamPage: params.page,
        ParamAppointmentId: params.appointmentId,
        ParamConsultingFor: params.consultingFor,
        consultationId: params.consultationId,
        status: params.status,
        patientId,

        // State
        notes, setNotes,
        isEditingChiefComplaint, setIsEditingChiefComplaint,
        tempChiefComplaint, setTempChiefComplaint,
        showStatusModal, setShowStatusModal,
        pendingSubmission,

        // Redux Data
        medicineList, loader, MedicalDetail, CcPaperDetail, ConsultationMasterData, AppointmentData, userDetails, medicalDraft, medicalDraftLoader,

        // Derived State
        IsFilledForm, patientInfo,

        // Functions
        initialValues,
        handleFormSubmit,
        handleStatusSelection,
        handleCancelStatusModal,
        handleUpdatePatientForm,
    };
};
